<!-- Демонстрация различных staggered анимаций -->
<template>
  <div class="staggered-animations-demo">
    <div class="container-fluid">
      <h2 class="mb-4">🎭 Демонстрация анимаций графиков</h2>
      
      <!-- Панель управления -->
      <div class="control-panel mb-4">
        <!-- Селектор анимации -->
        <div class="animation-selector mb-3">
          <h4>🎭 Выберите тип анимации:</h4>
          <div class="btn-group" role="group">
            <button 
              v-for="animation in availableAnimations" 
              :key="animation.key"
              type="button" 
              class="btn btn-outline-primary btn-sm"
              :class="{ active: selectedAnimation === animation.key }"
              @click="selectedAnimation = animation.key; refreshCharts()"
            >
              {{ animation.name }}
            </button>
          </div>
          <p class="mt-2 text-muted">{{ getCurrentAnimationDescription() }}</p>
        </div>

        <!-- Управление графиками -->
        <div class="chart-controls mb-3">
          <h4>🎮 Управление графиками:</h4>
          <div class="row">
            <div class="col-md-6">
              <h5>📈 Line Chart:</h5>
              <div class="btn-group mb-2" role="group">
                <button type="button" class="btn btn-success btn-sm" @click="refreshLineChart">
                  🔄 Обновить график
                </button>
                <button type="button" class="btn btn-warning btn-sm" @click="updateLineData">
                  📊 Новые данные
                </button>
                <button type="button" class="btn btn-info btn-sm" @click="triggerLineAnimation">
                  ⚡ Запустить анимацию
                </button>
              </div>
              <div class="dataset-toggles">
                <div class="form-check form-check-inline">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :id="'line-dataset-1'"
                    v-model="lineDataset1Visible"
                    @change="toggleLineDataset(0)"
                  >
                  <label class="form-check-label" :for="'line-dataset-1'">
                    Основной период
                  </label>
                </div>
                <div class="form-check form-check-inline">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :id="'line-dataset-2'"
                    v-model="lineDataset2Visible"
                    @change="toggleLineDataset(1)"
                  >
                  <label class="form-check-label" :for="'line-dataset-2'">
                    Сравнительный период
                  </label>
                </div>
              </div>
            </div>
            
            <div class="col-md-6">
              <h5>🫧 Bubble Chart:</h5>
              <div class="btn-group mb-2" role="group">
                <button type="button" class="btn btn-success btn-sm" @click="refreshBubbleChart">
                  🔄 Обновить график
                </button>
                <button type="button" class="btn btn-warning btn-sm" @click="updateBubbleData">
                  📊 Новые данные
                </button>
                <button type="button" class="btn btn-info btn-sm" @click="triggerBubbleAnimation">
                  ⚡ Запустить анимацию
                </button>
              </div>
              <div class="dataset-toggles">
                <div class="form-check form-check-inline">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :id="'bubble-dataset-1'"
                    v-model="bubbleDataset1Visible"
                    @change="toggleBubbleDataset(0)"
                  >
                  <label class="form-check-label" :for="'bubble-dataset-1'">
                    Датасет 1
                  </label>
                </div>
                <div class="form-check form-check-inline">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :id="'bubble-dataset-2'"
                    v-model="bubbleDataset2Visible"
                    @change="toggleBubbleDataset(1)"
                  >
                  <label class="form-check-label" :for="'bubble-dataset-2'">
                    Датасет 2
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Графики с выбранной анимацией -->
      <div class="row">
        <div class="col-md-6 mb-4">
          <div class="demo-section">
            <h4>📈 Line Chart (Линейный график)</h4>
            <div class="chart-container">
              <line-chart-template
                :chartData="lineChartData"
                :options="lineChartOptions"
                :animation-type="selectedAnimation"
                :key="`line-chart-${lineChartKey}`"
                class="demo-chart"
              />
            </div>
          </div>
        </div>
        
        <div class="col-md-6 mb-4">
          <div class="demo-section">
            <h4>🫧 Bubble Chart (Пузырьковый график)</h4>
            <div class="chart-container">
              <chart-pivot-template
                :chart-data="bubbleChartData"
                :options="bubbleChartOptions"
                :animation-type="selectedAnimation"
                :key="`bubble-chart-${bubbleChartKey}`"
                class="demo-chart"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Информация о текущей анимации -->
      <div class="animation-info mt-4">
        <div class="card">
          <div class="card-header">
            <h5>ℹ️ Информация о текущей анимации</h5>
          </div>
          <div class="card-body">
            <p><strong>Тип:</strong> {{ getCurrentAnimationConfig().name || selectedAnimation }}</p>
            <p><strong>Длительность:</strong> {{ getCurrentAnimationConfig().duration }}ms</p>
            <p><strong>Easing:</strong> {{ getCurrentAnimationConfig().easing }}</p>
            <p><strong>Описание:</strong> {{ getCurrentAnimationDescription() }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LineChartTemplate from '../apps/positions/reports/LineChart.vue';
import ChartPivotTemplate from '../apps/positions/reports/PivotChart.vue';
import { availableAnimations, chartAnimations } from '../utils/chartAnimations';

export default {
  name: 'StaggeredAnimationsDemo',
  components: {
    LineChartTemplate,
    ChartPivotTemplate
  },
  data() {
    return {
      selectedAnimation: 'smooth',
      availableAnimations: availableAnimations,
      // Ключи для принудительного обновления графиков
      lineChartKey: 0,
      bubbleChartKey: 0,
      // Видимость датасетов
      lineDataset1Visible: true,
      lineDataset2Visible: true,
      bubbleDataset1Visible: true,
      bubbleDataset2Visible: true,
      lineChartData: {
        labels: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн'],
        datasets: [
          {
            label: 'Основной период',
            data: [12, 19, 3, 5, 2, 3],
            borderColor: '#4CAF50',
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            tension: 0.4
          },
          {
            label: 'Сравнительный период',
            data: [2, 3, 20, 5, 1, 4],
            borderColor: '#FF9800',
            backgroundColor: 'rgba(255, 152, 0, 0.1)',
            tension: 0.4
          }
        ]
      },
      bubbleChartData: {
        datasets: [
          {
            label: 'Датасет 1',
            data: [
              { x: 1, y: 2, r: 10 },
              { x: 2, y: 3, r: 15 },
              { x: 3, y: 1, r: 8 },
              { x: 4, y: 4, r: 12 }
            ],
            backgroundColor: 'rgba(255, 99, 132, 0.6)',
            borderColor: 'rgba(255, 99, 132, 1)'
          },
          {
            label: 'Датасет 2',
            data: [
              { x: 1, y: 3, r: 12 },
              { x: 2, y: 1, r: 9 },
              { x: 3, y: 4, r: 14 },
              { x: 4, y: 2, r: 11 }
            ],
            backgroundColor: 'rgba(54, 162, 235, 0.6)',
            borderColor: 'rgba(54, 162, 235, 1)'
          }
        ]
      },
      lineChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true
          }
        }
      },
      bubbleChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true
          },
          x: {
            beginAtZero: true
          }
        }
      }
    };
  },
  methods: {
    getCurrentAnimationConfig() {
      return chartAnimations[this.selectedAnimation] || chartAnimations.smooth;
    },
    getCurrentAnimationDescription() {
      const animation = this.availableAnimations.find(a => a.key === this.selectedAnimation);
      return animation ? animation.description : 'Описание недоступно';
    },
    refreshCharts() {
      // Принудительно обновляем графики для применения новой анимации
      this.$forceUpdate();
    },
    
    // Методы для управления Line Chart
    refreshLineChart() {
      this.lineChartKey++;
    },
    updateLineData() {
      // Генерируем новые случайные данные
      this.lineChartData.datasets[0].data = this.generateRandomData(6, 1, 20);
      this.lineChartData.datasets[1].data = this.generateRandomData(6, 1, 20);
      this.lineChartKey++;
    },
    triggerLineAnimation() {
      this.lineChartKey++;
    },
    toggleLineDataset(index) {
      if (index === 0) {
        this.lineChartData.datasets[0].hidden = !this.lineDataset1Visible;
      } else {
        this.lineChartData.datasets[1].hidden = !this.lineDataset2Visible;
      }
      this.lineChartKey++;
    },
    
    // Методы для управления Bubble Chart
    refreshBubbleChart() {
      this.bubbleChartKey++;
    },
    updateBubbleData() {
      // Генерируем новые случайные данные для пузырьков
      this.bubbleChartData.datasets[0].data = this.generateRandomBubbleData(4);
      this.bubbleChartData.datasets[1].data = this.generateRandomBubbleData(4);
      this.bubbleChartKey++;
    },
    triggerBubbleAnimation() {
      this.bubbleChartKey++;
    },
    toggleBubbleDataset(index) {
      if (index === 0) {
        this.bubbleChartData.datasets[0].hidden = !this.bubbleDataset1Visible;
      } else {
        this.bubbleChartData.datasets[1].hidden = !this.bubbleDataset2Visible;
      }
      this.bubbleChartKey++;
    },
    
    // Вспомогательные методы
    generateRandomData(count, min, max) {
      return Array.from({ length: count }, () => 
        Math.floor(Math.random() * (max - min + 1)) + min
      );
    },
    generateRandomBubbleData(count) {
      return Array.from({ length: count }, (_, i) => ({
        x: i + 1,
        y: Math.floor(Math.random() * 5) + 1,
        r: Math.floor(Math.random() * 10) + 5
      }));
    }
  }
};
</script>

<style scoped>
.staggered-animations-demo {
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
}

.animation-selector {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.chart-controls {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.btn-group .btn {
  margin-right: 5px;
  margin-bottom: 5px;
}

.dataset-toggles {
  margin-top: 10px;
}

.dataset-toggles .form-check {
  margin-right: 15px;
}

.form-check-label {
  font-size: 14px;
  color: #495057;
}

.btn-group .btn {
  margin-right: 5px;
  margin-bottom: 5px;
}

.demo-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  height: 100%;
}

.chart-container {
  height: 300px;
  position: relative;
}

.demo-chart {
  height: 100%;
  width: 100%;
}

.animation-info {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.card-body p {
  margin-bottom: 10px;
}

.card-body p:last-child {
  margin-bottom: 0;
}
</style>
